import { useEffect, useState } from "react";
import styles from "./ConfirmShipmentsPage.module.css";
import IconInfo from "@/components/icons/IconInfo";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { amazonAddressToStringSingleLine } from "../../utils";
import { getTransportationOptions } from "@/api/fulfillment";
import RadioInput from "@/components/forms/RadioInput";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { PlacementOption as ShipmentContextPlacementOption, useShipmentContext } from "@/contexts/ShipmentContext";
import { TransportType } from "@boxem/core-api-schema";
import Flex from "@/components/Flex/Flex";
import { formatValueAsCurrency } from "@/utils/formatUtils";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import IconCaution from "@/components/icons/IconCaution";
import IconCheckCircle from "@/components/icons/IconCheckCircle";

type PlacementOptionProps = {
  placementOption: ShipmentContextPlacementOption;
  selectedPlacementOptionId: string;
  setSelectedPlacementOptionId: (id: string | null) => void;
};

const PLACEMENT_OPTION_RADIO_NAME = "placement_option";

export default function PlacementOption({
  placementOption,
  selectedPlacementOptionId,
  setSelectedPlacementOptionId,
}: PlacementOptionProps) {
  const { currentBatch, setCurrentBatch } = useShipmentContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();

  const [transportationOptionsLoading, setTransportationOptionsLoading] = useState<boolean>(false);
  const [loadingFailed, setLoadingFailed] = useState<boolean>(false);

  const optPlacement = currentBatch.fees[placementOption.placementOptionId]?.placement === 0;
  
  const setSelectedPlacementOption = (placementOption: ShipmentContextPlacementOption) => {
    if (placementOption.isExpired) return;
    setSelectedPlacementOptionId(placementOption.placementOptionId);
  };
  useEffect(() => {
    if (currentBatch.shipmentType === TransportType.SPD && placementOption && !placementOption.shipmentOptions.every(so => Array.isArray(so.transportationOptions) && so.transportationOptions.length > 0)) {
      setTransportationOptionsLoading(true);
      const id = currentBatch.id;
      getTransportationOptions(placementOption.placementOptionId).then(result => {
        if (result.errors) {
          handleErrorResponseGenerically(result.errors);
          setLoadingFailed(true);
          return;
        }
        if (currentBatch.id !== id) return; // we navigated away or changed the focused batch mid-poll. Do nothing.
        placementOption.shipmentOptions.forEach(so => {
          const transportationOptions = result.data.find(options => options.shipmentId === so.shipmentId);
          if (transportationOptions) {
            so.transportationOptions = transportationOptions.transportationOptions;
          }
        });
        setCurrentBatch({...currentBatch});
      })
      .catch(e => {
        handleErrorResponseGenerically(e);
        setLoadingFailed(true);
      })
      .finally(() => setTransportationOptionsLoading(false));
    }
  }, [placementOption]);

  const placementFees = <>
    <span className={styles.fee}>Total placement fees: </span>
    <span className={`${styles.right} ${styles.fee}`}>
      {optPlacement && <span className="inline-positive"><IconCheckCircle />&nbsp;Optimized Placement</span>}
      <span className={optPlacement ? "positive" : "strong"}>
        &nbsp;{formatValueAsCurrency(currentBatch.fees[placementOption.placementOptionId]?.placement)} USD
      </span>
    </span>
  </>;

  const totalFees = currentBatch.shipmentType === TransportType.SPD
    ? <>
      <span className={styles.fee}>Total fees: </span>
      <span className={`${styles.right} ${styles.fee}`}>{formatValueAsCurrency(currentBatch.fees[placementOption.placementOptionId]?.total)} USD</span>
    </>
    : placementFees;

  return (
    <Tooltip
      title={placementOption.isExpired ? "This placement option has expired" : ""}
      delayMs={0}
    >
      <div
        className={`
          ${styles.sectionContainer}
          ${styles.placementOption}
          ${selectedPlacementOptionId === placementOption.placementOptionId ? styles.active : ""}
          ${placementOption.isExpired ? styles.disabled : ''}
        `}
        onClick={() => setSelectedPlacementOption(placementOption)}
      >
        <div className={`${styles.row} ${styles.sectionContent} ${styles.header}`}>
          <RadioInput
            name={PLACEMENT_OPTION_RADIO_NAME}
            value={placementOption.placementOptionId}
            checked={selectedPlacementOptionId === placementOption.placementOptionId}
            onChange={() => setSelectedPlacementOption(placementOption)}
          />
          <h1>{placementOption.shipmentOptions.length} Shipment{placementOption.shipmentOptions.length !== 1 && "s"}</h1>
          <span className={styles.spacer} />
          {!!placementOption?.expiration && (
            <span className="inline-notice">
              <IconCaution />
              &nbsp;Expires at:&nbsp;
              {new Date(placementOption?.expiration).toLocaleDateString()}
            </span>
          )}
        </div>
        {currentBatch.shipmentType === TransportType.SPD &&
          <div className={`${styles.row} ${styles.shipmentDestinations} ${styles.spd}`}>
            {placementOption.shipmentOptions.map((so, soIndex) => (
              <span key={soIndex} className={`${styles.shipmentDestination} ${styles.spd}`}>
                <span className="small-text weak" style={{alignSelf: "flex-end"}}>Ship to:</span>
                <span className="strong">{so.fulfillmentCenter.warehouseId}</span>
                <Tooltip
                  placement="right"
                  title={amazonAddressToStringSingleLine(so.fulfillmentCenter.address)}
                >
                  <span className={styles.icon}><IconInfo /></span>
                </Tooltip>
              </span>
            ))}
          </div>
        }
        {currentBatch.shipmentType === TransportType.SPD && (
          <div className={`${styles.fees} ${styles.grid} ${styles.sectionContent}`}>
            <span className="weak">Total prep and labeling fees: </span>
            <span className={`${styles.right} strong`}>{formatValueAsCurrency(currentBatch.fees[placementOption.placementOptionId]?.prep)} USD</span>
            {placementFees}
            <span className="weak">Total estimated shipping fees: </span>
            {transportationOptionsLoading 
              ? (
                <Flex align="center" gap={2}>
                  <span className={styles.spacer}></span>
                  <LoadingSpinner size="18px"/>
                </Flex>
              ) : loadingFailed
              ? (
                <span className={styles.right} style={{color: "var(--sentiment-text-negative"}}>
                  <Tooltip title="Fees failed to load. Try refreshing the page.">
                    <IconCaution currentColor={true}/>
                  </Tooltip>
                </span>
              )
              : <span className={`${styles.right} strong`}>{formatValueAsCurrency(currentBatch.fees[placementOption.placementOptionId]?.transportation)} USD</span>
            }
          </div>
        )}
        {currentBatch.shipmentType === TransportType.LTL && (
          <div className={`${styles.sectionContent} ${styles.shipmentDestinations} ${styles.ltl}`}>
            {placementOption.shipmentOptions.map((so, soIndex) => (
              <div key={soIndex} className={`${styles.shipmentDestination} ${styles.ltl}`}>
                <span className="strong">#{soIndex + 1}</span>
                <span className="small-text weak" style={{alignSelf: "flex-end"}}> - Ship to: </span>
                <span className="strong">{so.fulfillmentCenter.warehouseId} </span>
                <span className="small-text weak">{amazonAddressToStringSingleLine(so.fulfillmentCenter.address)}</span>
              </div>
            ))}
          </div>
        )}
        <div className={`${styles.row} ${styles.sectionContent} ${styles.footer} ${styles.grid} ${styles.totalFees}`}>
          {totalFees}
        </div>
      </div>
    </Tooltip>
  );
}
